import type { AdminRole, Membership } from "./types";

/**
 * Single source of truth for "which admin panel does this role belong to".
 *
 * Each admin panel is a SEPARATE Firebase Hosting site, so switching a
 * multi-role user's active membership is not enough — we must also send the
 * browser to the panel that owns the new role. This map answers both halves:
 * the human label of the panel and its base address for the current
 * environment.
 *
 * Address resolution order for a panel:
 *   1. `VITE_PANEL_URL_<PANEL>` build-time env var (set per environment)
 *   2. same-origin path prefix, when running on a dev host (localhost /
 *      *.replit.dev) where all panels are served under path prefixes
 *   3. `null` — panel address is NOT configured; callers must surface a clear
 *      message instead of navigating somewhere broken.
 */

export type PanelId = "katana-admin" | "chain-admin" | "pharmacy-admin" | "mobile";

export interface PanelInfo {
  id: PanelId;
  /** Human label, e.g. "Katana Admin". */
  label: string;
  /**
   * Absolute URL or same-origin path (always ends with "/") for the panel,
   * or null when the panel's address is not configured for this environment.
   */
  baseUrl: string | null;
  /** True for the mobile app "panel", which has no web address at all. */
  mobileOnly: boolean;
}

const PANEL_LABELS: Record<PanelId, string> = {
  "katana-admin": "Katana Admin",
  "chain-admin": "Chain Admin",
  "pharmacy-admin": "Pharmacy Admin",
  mobile: "CCS Pharmacy mobile app",
};

const PANEL_ENV_VARS: Record<Exclude<PanelId, "mobile">, string> = {
  "katana-admin": "VITE_PANEL_URL_KATANA_ADMIN",
  "chain-admin": "VITE_PANEL_URL_CHAIN_ADMIN",
  "pharmacy-admin": "VITE_PANEL_URL_PHARMACY_ADMIN",
};

const ROLE_PANEL: Record<AdminRole, PanelId> = {
  katana_admin: "katana-admin",
  pharmacy_chain_admin: "chain-admin",
  pharmacy_admin: "pharmacy-admin",
  pharmacist: "mobile",
};

/** The panel a given role belongs to. */
export function panelIdForRole(role: AdminRole): PanelId {
  return ROLE_PANEL[role] ?? "mobile";
}

function readEnv(name: string): string | undefined {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  const v = env?.[name];
  return v && v.length > 0 ? v : undefined;
}

function isDevHost(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h.endsWith(".replit.dev") ||
    h.endsWith(".repl.co") ||
    h.endsWith(".picard.replit.dev")
  );
}

function withTrailingSlash(url: string): string {
  return url.endsWith("/") ? url : `${url}/`;
}

function resolveBaseUrl(id: PanelId): string | null {
  if (id === "mobile") return null;
  const fromEnv = readEnv(PANEL_ENV_VARS[id]);
  if (fromEnv) return withTrailingSlash(fromEnv);
  // Replit dev preview: each artifact's Vite server itself runs at
  // BASE_PATH="/" on its own port, but the shared Replit proxy fronts every
  // artifact under a path prefix on one shared dev domain — e.g. this
  // project's Chain Admin artifact has previewPath "/chain-admin/". That
  // prefix always matches the panel id, so a same-origin "/<id>/" path is
  // the correct address for cross-panel navigation while previewing on
  // Replit, without needing per-panel env vars in dev.
  if (isDevHost()) return `/${id}/`;
  return null;
}

/** Panel metadata (label + resolved address) for a role. */
export function panelForRole(role: AdminRole): PanelInfo {
  const id = panelIdForRole(role);
  return {
    id,
    label: PANEL_LABELS[id],
    baseUrl: resolveBaseUrl(id),
    mobileOnly: id === "mobile",
  };
}

/** Panel metadata by id — useful for labelling the current panel. */
export function panelById(id: PanelId): PanelInfo {
  return {
    id,
    label: PANEL_LABELS[id],
    baseUrl: resolveBaseUrl(id),
    mobileOnly: id === "mobile",
  };
}

/** True when the membership's role is served by the given panel. */
export function membershipBelongsToPanel(m: Membership, panel: PanelId): boolean {
  return panelIdForRole(m.role) === panel;
}
